<template>
  <form v-on:submit.prevent="submit">
    <h1 v-if="id" class="title">Edit Recipe</h1>
    <div class="field">
      <label class="radio">
        <input type="radio" value="private" v-model="form.access">
        {{$t('private')}}
      </label>
      <label class="radio">
        <input type="radio" value="public" v-model="form.access">
        {{$t('public')}}
      </label>
    </div>

    <div class="columns">
      <div class="column">
        <div class="field">
          <label class="label">Name</label>
          <div class="control">
            <input v-model="form.name" ref="name" class="input" type="text">
          </div>
        </div>

        <div v-if="images.length" class="field">
          <label class="label">Images</label>
          <div class="control">
            <div ref="imagesBox" class="box">
              <div v-for="img in images" :key="img.id" class="image-grid">
                <figure class="image my-image" :style="img.style">
                  <img :src="img.data">
                </figure>

                <div class="image-overlay" :style="img.style">
                  <span class="icon delete-image" @click.prevent="deleteImage(img)">
                    <i class="far fa-trash-alt"></i>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="field">
          <label class="label">Link</label>
          <div class="control">
            <input v-model="form.link" ref="link" class="input" type="url">
          </div>
        </div>

        <div class="field">
          <label class="label">Description</label>
          <div class="control">
            <textarea v-model="form.description" ref="description" class="textarea">
            </textarea>
          </div>
        </div>
      </div>

      <div class="column">
        <div class="field"><div class="control"><label>&nbsp;</label></div></div>
        <b-field v-if="!captureMode && !uploadMode"
          grouped
          position="is-centered">
          <p class="control">
            <button type="button" class="button" @click.prevent="captureMode = true">
              <span class="icon">
                <i class="fas fa-camera"></i>
              </span>
              <span>Take Pic</span>
            </button>
          </p>

          <p class="control">
            <button type="button" class="button" @click.prevent="uploadMode = true">
              <span class="icon">
                <i class="fas fa-cloud-upload-alt "></i>
              </span>
              <span>Upload Pics</span>
            </button>
          </p>
        </b-field>

        <div v-if="captureMode">
          <image-capture @save="saveCapturedImage" @cancel="captureMode = false">
          </image-capture>
        </div>

        <div v-if="uploadMode">
          <file-upload @done="uploadMode = false">
          </file-upload>
        </div>
      </div>
    </div>

    <new-categories :available="categoryNames" v-model="form.categories"></new-categories>

    <div class="columns">
      <div class="column">
        <div class="field">
          <label class="label">Ingredients</label>
          <div class="control">
            <textarea v-model="ingredientsString" ref="ingredients" class="textarea"
            placeholder="One per line">
            </textarea>
          </div>
        </div>
      </div>

      <div class="column">
        <div class="field">
          <label class="label">Directions</label>
          <div class="control">
            <textarea v-model="directionsString" ref="directions" class="textarea"
            placeholder="One per line">
            </textarea>
          </div>
        </div>
      </div>
    </div>

    <div class="columns">
      <div class="column">
        <label class="label">Prep Time</label>
        <div class="field is-horizontal">
          <div class="field-body">
            <div class="field">
              <div class="control">
                <input
                  v-model="prepTime.days"
                  class="input"
                  type="number"
                  placeholder="days"
                  min="0"
                  max="99">
              </div>
            </div>

            <div class="field">
              <div class="control">
                <input
                  v-model="prepTime.hours"
                  class="input"
                  type="number"
                  placeholder="hours"
                  min="0"
                  max="23">
              </div>
            </div>

            <div class="field">
              <div class="control">
                <input
                  v-model="prepTime.mins"
                  class="input"
                  type="number"
                  placeholder="mins"
                  min="0"
                  max="59">
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="column">
        <div class="field">
          <label class="label">Servings</label>
          <div class="control">
            <input v-model="form.servings" class="input" type="number" placeholder="#">
          </div>
        </div>
      </div>
    </div>

    <div class="field is-grouped is-grouped-centered">
      <p class="control">
        <button type="submit" class="button is-primary">
          Submit
        </button>
      </p>
      <p class="control">
        <a class="button is-warning" @click="confirmReset">
          Reset
        </a>
      </p>
      <p class="control">
        <a class="button is-light" @click="goBack">
          Back
        </a>
      </p>
    </div>
  </form>
</template>

<i18n src="../../i18n/access.i18n.json"></i18n>

<script>
import _ from 'lodash';
import { mapGetters } from 'vuex';
import v4 from 'uuid/v4';
import routingMixin from '@/mixins/routing';
import i18nMixin from '@/mixins/i18n';
import NewCategories from '../NewCategories';
import ImageCapture from '../ImageCapture';
import FileUpload from '../FileUpload';
import { toMinutes, fromMinutes } from '../../utils/time';

function copyString(src, defValue = '') {
  return src ? src.slice() : defValue;
}

function copyArray(src, defValue = []) {
  // shallow
  return src ? src.slice() : defValue;
}

class FormData {
  constructor(o = {}) {
    this.name = copyString(o.name);
    this.link = copyString(o.link);
    this.access = copyString(o.access, 'private');
    this.description = copyString(o.description);
    this.categories = copyArray(o.categories);
    this.ingredients = copyArray(o.ingredients);
    this.directions = copyArray(o.directions);
    this.servings = o.servings;
    this.prepTimeMin = o.prepTimeMin;
  }
}

export default {
  name: 'recipe-form',
  mixins: [routingMixin, i18nMixin],
  props: {
    id: {
      // Edit mode if this is set!
      type: String,
      required: false,
    },
  },
  data() {
    return {
      form: new FormData(),

      images: [],

      ingredientsString: '',
      directionsString: '',
      prepTime: {
        days: null,
        hours: null,
        mins: null,
      },

      captureMode: false,
      capturedImage: null,

      uploadMode: false,
    };
  },
  mounted() {
    this.$nextTick(() => this.resetForm());
  },
  methods: {
    submit() {
      this.form.ingredients = this.ingredientsString.split(/\n+/);
      this.form.directions = this.directionsString.split(/\n+/);
      this.form.prepTimeMin = toMinutes(this.prepTime);

      // TODO add validation!
      // TODO add validation!

      if (this.id) {
        this.$store.dispatch('recipe/updateRecipe', { id: this.id, recipe: this.form }).then((returnedRecipe) => {
          this.routeFoodItem({ type: 'Recipe', id: returnedRecipe.id, noBack: true });
        })
        .catch(err => this.$emit('flash', err.message));
      } else {
        this.$store.dispatch('recipe/saveRecipe', this.form).then((id) => {
          this.routeFoodItem({ type: 'Recipe', id, noBack: true });
        })
        .catch(err => this.$emit('flash', err.message));
      }
    },
    confirmReset() {
      const self = this;
      this.$dialog.confirm({
        title: 'Reset Form',
        message: 'Are you sure?',
        confirmText: 'Reset',
        type: 'is-danger',
        hasIcon: true,
        onConfirm() {
          self.resetForm();
        },
      });
    },
    resetForm() {
      this.captureMode = false;
      this.uploadMode = false;
      this.capturedImage = null;

      const self = this;
      let promise;
      if (this.id) {
        const loading = this.$loading.open();
        promise = this.$store.dispatch('recipe/fetchRecipe', this.id)
        .then(() => {
          // recipe is frozen, because it's in the graphql store
          self.form = new FormData(self.recipe);
          self.ingredientsString = self.form.ingredients.join('\n');
          self.directionsString = self.form.directions.join('\n');
          self.prepTime = fromMinutes(self.form.prepTimeMin);
          loading.close();
        })
        .catch((err) => {
          self.error = err.message;
        });
      } else {
        this.form = new FormData();
        promise = Promise.resolve();
      }

      return promise.then(() => self.$refs.name.focus());
    },
    saveCapturedImage(image) {
      if (image.data) {
        const height = image.height / (image.width / 128);
        this.images.push(_.extend({
          id: v4(),
          style: {
            height: `${height}px`,
            width: '128px',
          },
        }, image));
      } else {
        this.$toast.open({
          message: 'Got image save with NO data',
          type: 'is-warning',
        });
      }
    },
    deleteImage(image) {
      this.$dialog.confirm({
        title: 'Delete image',
        message: 'Are you sure?',
        confirmText: 'Delete',
        type: 'is-danger',
        hasIcon: true,
        onConfirm: () => {
          const index = _.findIndex(this.images, { id: image.id });
          if (index >= 0) {
            this.images.splice(index, 1);
          }
        },
      });
    },
  },
  computed: {
    ...mapGetters(['categoryNames']),
    ...mapGetters('recipe', ['recipe']),
  },
  components: {
    NewCategories,
    ImageCapture,
    FileUpload,
  },
};

</script>

<style lang="sass" scoped>
.canvas
  display: none

.image-grid
  display: inline-flex
  position: relative
  z-index: 1

.image-overlay
  opacity: 0
  z-index: 2
  position: absolute
  margin: auto

.image-overlay:hover
  opacity: 1
  background: rgba(0, 0, 0, 0.5) center no-repeat

.my-image
  margin-right: 1em
  margin-bottom: 1em

.delete-image
  position: absolute
  left: 5px
  top: 5px
  background: white
</style>




// WEBPACK FOOTER //
// src/components/recipe/RecipeForm.vue